import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NgxImageCompressService } from 'ngx-image-compress';
import {
  ImageCroppedEvent,
  OutputFormat,
  ImageCropperComponent as NgxCropper,
} from 'ngx-image-cropper';
import { Card } from 'src/app/modules/templates/interfaces/create-template.interface';

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
})
export class ImageCropperComponent implements OnChanges, OnDestroy {
  @Input() file: any = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() selectedCard: Card;
  @Input() minSizeKB: number;
  @Input() maxSizeKB: number;
  @Input() set format(ext: OutputFormat) {
    this.extension = ext;
  }
  @Output() cropped = new EventEmitter<Blob>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild(NgxCropper, { static: false }) cropper: NgxCropper;

  loadCropper = false;
  aspectRatio: number;
  resizeToWidth: number;
  resizeToHeight: number;
  extension: OutputFormat = 'jpeg';

  croppedImage: Blob | null = null;
  croppedImageUrl: string | null = null;
  extensionMapper: any = {
    jpeg: 'jpeg',
    png: 'png',
    jpg: 'jpeg',
  };

  constructor(private readonly imageCompress: NgxImageCompressService) {}

  ngOnChanges() {
    if (this.selectedCard) {
      this.loadCropper = true;
      this.aspectRatio = this.selectedCard.aspectRatio;
      const [w, h] = this.selectedCard.dimensions.split('X');
      this.resizeToWidth = parseInt(w.replace('px', ''), 10);
      this.resizeToHeight = parseInt(h.replace('px', ''), 10);
    }
  }

  imageCropped = async (event: ImageCroppedEvent) => {
    const originalFile: File = this.file.target?.files?.[0];
    if (!originalFile) return;

    let blob = await this.enforceExactSize(
      event?.blob as Blob,
      this.resizeToWidth,
      this.resizeToHeight
    );

    const sizeKB = blob.size / 1024;
    if (sizeKB < this.minSizeKB || sizeKB > this.maxSizeKB) {
      blob = await this.getOptimalSizeBlob(
        blob,
        this.minSizeKB,
        this.maxSizeKB
      );
    }

    this.croppedImage = blob;
    this.croppedImageUrl = URL.createObjectURL(blob);
  };

  enforceExactSize(
    blob: Blob,
    targetW: number,
    targetH: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, targetW, targetH);
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Canvas empty'))),
          `image/${this.extensionMapper[this.extension]}`
        );
      };
      img.onerror = (e) => reject(e);
      img.src = URL.createObjectURL(blob);
    });
  }

  async onSubmit() {
    if (!this.croppedImage) return;
    const originalName = this.file.target?.files?.[0]?.name || 'cropped-image';
    const file = new File([this.croppedImage], originalName, {
      type: `image/${this.extensionMapper[this.extension]}`,
    });
    this.cropped.emit(file);
    this.close();
  }

  onCancel() {
    this.cancel.emit();
    this.close();
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  async getOptimalSizeBlob(
    blob: Blob,
    minKB: number,
    maxKB: number
  ): Promise<Blob> {
    const initialSizeKB = blob.size / 1024;
    console.log(
      `Initial size: ${initialSizeKB.toFixed(2)}KB, target: ${minKB}-${maxKB}KB`
    );

    // If already in range, return as-is
    if (initialSizeKB >= minKB && initialSizeKB <= maxKB) {
      console.log('Image already in target range');
      return blob;
    }

    // If image is smaller than minimum, increase its size by upscaling
    if (initialSizeKB < minKB) {
      console.log(
        `Image too small (${initialSizeKB.toFixed(
          2
        )}KB < ${minKB}KB). Increasing size by upscaling.`
      );
      return await this.increaseBlobSize(blob, minKB, maxKB);
    }

    // If image is larger than maximum, compress it
    if (initialSizeKB > maxKB) {
      console.log('Compressing image to fit within maximum size');
      return await this.compressToTargetSize(blob, maxKB, minKB);
    }

    return blob;
  }

  private async compressToTargetSize(
    blob: Blob,
    maxKB: number,
    minKB: number
  ): Promise<Blob> {
    const originalSizeKB = blob.size / 1024;
    console.log(
      `Starting compression: ${originalSizeKB.toFixed(
        2
      )}KB -> target: ${maxKB}KB`
    );

    // Get original image dimensions to maintain its aspect ratio
    const { width: originalWidth, height: originalHeight } =
      await this.getImageDimensions(blob);
    const originalAspectRatio = originalWidth / originalHeight;

    let bestBlob = blob;
    let bestSizeKB = originalSizeKB;

    // Optimized: Start with fewer, more strategic quality levels
    const qualityLevels = [85, 70, 55, 40, 25, 10];

    for (let i = 0; i < qualityLevels.length; i++) {
      const quality = qualityLevels[i];

      try {
        const dataUrl = await this.blobToDataURL(blob);
        const { maxWidth, maxHeight } = this.calculateMaxDimensions(
          originalAspectRatio,
          1200 // Base max dimension
        );

        const compressed = await this.imageCompress.compressFile(
          dataUrl,
          -1, // originalOrientation
          70, // Higher ratio for better quality at first attempts
          quality,
          maxWidth,
          maxHeight
        );

        const newBlob = this.convertBase64ToBlob(compressed);
        const sizeKB = newBlob.size / 1024;

        console.log(
          `Compression attempt ${
            i + 1
          }: Quality ${quality}%, Size: ${sizeKB.toFixed(2)}KB (reduction: ${(
            originalSizeKB - sizeKB
          ).toFixed(2)}KB)`
        );

        // Update best result if this one is better
        if (sizeKB < bestSizeKB) {
          bestBlob = newBlob;
          bestSizeKB = sizeKB;
        }

        // If we're in the target range, return this result
        if (sizeKB >= minKB && sizeKB <= maxKB) {
          console.log(
            `Found optimal compressed size: ${sizeKB.toFixed(
              2
            )}KB at quality ${quality}%`
          );
          return newBlob;
        }

        // If we've compressed below maxKB, we can stop looking for more compression
        if (sizeKB <= maxKB) {
          console.log(
            `Achieved target compression: ${sizeKB.toFixed(2)}KB <= ${maxKB}KB`
          );
          return newBlob;
        }
      } catch (error) {
        console.error(`Compression failed at quality ${quality}%:`, error);
        continue; // Try next quality level
      }
    }

    // If we still haven't reached target, try more aggressive compression
    if (bestSizeKB > maxKB) {
      console.log(
        'Trying more aggressive compression with smaller dimensions...'
      );

      const aggressiveBlob = await this.performAggressiveCompression(
        blob,
        originalAspectRatio,
        minKB
      );

      if (aggressiveBlob && aggressiveBlob.size / 1024 < bestSizeKB) {
        bestBlob = aggressiveBlob;
        bestSizeKB = aggressiveBlob.size / 1024;
      }
    }

    console.log(
      `Final compressed size: ${bestSizeKB.toFixed(2)}KB (reduced by ${(
        originalSizeKB - bestSizeKB
      ).toFixed(2)}KB)`
    );
    return bestBlob;
  }

  private async getImageDimensions(
    blob: Blob
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () =>
        reject(new Error('Failed to load image for dimension calculation'));
      img.src = URL.createObjectURL(blob);
    });
  }

  private calculateMaxDimensions(
    aspectRatio: number,
    maxDimension: number
  ): { maxWidth: number; maxHeight: number } {
    if (aspectRatio > 1) {
      // Landscape: width is larger
      return {
        maxWidth: maxDimension,
        maxHeight: Math.round(maxDimension / aspectRatio),
      };
    } else {
      // Portrait or square: height is larger or equal
      return {
        maxWidth: Math.round(maxDimension * aspectRatio),
        maxHeight: maxDimension,
      };
    }
  }

  private async performAggressiveCompression(
    blob: Blob,
    aspectRatio: number,
    minKB: number
  ): Promise<Blob | null> {
    try {
      const dataUrl = await this.blobToDataURL(blob);
      const { maxWidth, maxHeight } = this.calculateMaxDimensions(
        aspectRatio,
        600
      );

      const aggressiveCompressed = await this.imageCompress.compressFile(
        dataUrl,
        -1, // originalOrientation
        40, // moderate ratio for aggressive compression
        8, // very low quality
        maxWidth,
        maxHeight
      );

      const aggressiveBlob = this.convertBase64ToBlob(aggressiveCompressed);
      const aggressiveSizeKB = aggressiveBlob.size / 1024;

      console.log(
        `Aggressive compression result: ${aggressiveSizeKB.toFixed(2)}KB`
      );

      return aggressiveSizeKB >= minKB ? aggressiveBlob : null;
    } catch (error) {
      console.error('Aggressive compression failed:', error);
      return null;
    }
  }

  private async increaseBlobSize(
    blob: Blob,
    minKB: number,
    maxKB: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const currentSizeKB = blob.size / 1024;
          let scaleFactor = Math.sqrt(minKB / currentSizeKB); // Start with mathematical scale factor
          let bestBlob = blob;
          let bestSizeKB = currentSizeKB;

          // Try different scale factors to find optimal size
          for (let i = 0; i < 10; i++) {
            const newWidth = Math.round(img.naturalWidth * scaleFactor);
            const newHeight = Math.round(img.naturalHeight * scaleFactor);

            console.log(
              `Upscaling attempt ${i + 1}: Scale ${scaleFactor.toFixed(
                2
              )}x, Size: ${newWidth}x${newHeight}`
            );

            const canvas = document.createElement('canvas');
            canvas.width = newWidth;
            canvas.height = newHeight;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            // Use high-quality scaling
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, newWidth, newHeight);

            // Convert canvas to blob with high quality
            const upscaledBlob = await new Promise<Blob | null>(
              (canvasResolve) => {
                canvas.toBlob(
                  canvasResolve,
                  `image/${this.extensionMapper[this.extension]}`,
                  0.95 // High quality
                );
              }
            );

            if (!upscaledBlob) {
              console.warn('Failed to create upscaled blob');
              continue;
            }

            const newSizeKB = upscaledBlob.size / 1024;
            console.log(
              `Upscaled size: ${newSizeKB.toFixed(
                2
              )}KB (target: ${minKB}-${maxKB}KB)`
            );

            // If we're in the target range, return this result
            if (newSizeKB >= minKB && newSizeKB <= maxKB) {
              console.log(
                `Found optimal upscaled size: ${newSizeKB.toFixed(2)}KB`
              );
              resolve(upscaledBlob);
              return;
            }

            // Update best result if it's closer to target
            if (
              (newSizeKB >= minKB && newSizeKB < bestSizeKB) ||
              (bestSizeKB < minKB && newSizeKB > bestSizeKB)
            ) {
              bestBlob = upscaledBlob;
              bestSizeKB = newSizeKB;
            }

            // If we exceeded maxKB, try to compress it
            if (newSizeKB > maxKB) {
              console.log('Upscaled image too large, compressing...');
              const compressedBlob = await this.compressToTargetSize(
                upscaledBlob,
                maxKB,
                minKB
              );
              const compressedSizeKB = compressedBlob.size / 1024;

              if (compressedSizeKB >= minKB && compressedSizeKB <= maxKB) {
                console.log(
                  `Found optimal compressed upscaled size: ${compressedSizeKB.toFixed(
                    2
                  )}KB`
                );
                resolve(compressedBlob);
                return;
              }

              if (compressedSizeKB >= minKB) {
                bestBlob = compressedBlob;
                bestSizeKB = compressedSizeKB;
              }
            }

            // Adjust scale factor for next iteration
            if (newSizeKB < minKB) {
              scaleFactor *= 1.2; // Increase scale factor
            } else if (newSizeKB > maxKB) {
              scaleFactor *= 0.9; // Decrease scale factor
            } else {
              break; // We're in range
            }
          }

          console.log(
            `Final upscaled size: ${bestSizeKB.toFixed(2)}KB (best achievable)`
          );
          resolve(bestBlob);
        } catch (error) {
          console.error('Error during upscaling:', error);
          reject(error);
        }
      };

      img.onerror = (error) => {
        console.error('Error loading image for upscaling:', error);
        reject(error);
      };

      img.src = URL.createObjectURL(blob);
    });
  }

  blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(blob);
    });
  }

  convertBase64ToBlob(base64: string): Blob {
    const [header, data] = base64.split(',');
    const mime =
      header.match(/:([^;]{1,1000});/u)?.[1] ||
      `image/${this.extensionMapper[this.extension]}`;
    const binary = atob(data);
    const buffer = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
    return new Blob([buffer], { type: mime });
  }

  ngOnDestroy() {
    if (this.croppedImageUrl) URL.revokeObjectURL(this.croppedImageUrl);
  }
}
